import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import { useAuth } from "../../contexts/AuthContext";
import { BankDetailsDto, useFeeStructure } from "../../hooks/useFeeStructure";

interface BankDetailsModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (formData: BankDetailsDto) => void;
  schoolId?: number;
}

const BankDetailsModal: React.FC<BankDetailsModalProps> = ({
  open,
  handleClose,
  onSubmit,
  schoolId,
}) => {
  const { t } = useTranslation();
  const { showError } = useError();
  const { user, isTeacher } = useAuth();

  // Get school id (sent as `id` in payload)
  const finalSchoolId = useMemo(() => {
    if (schoolId) return schoolId;
    if (isTeacher()) {
      return user?.schoolId;
    }
    return user?.schoolId;
  }, [schoolId, user?.schoolId, isTeacher]);

  // Convert null to undefined for the hook (hook expects number | undefined, not null)
  const schoolIdForHook = useMemo(() => {
    return finalSchoolId ?? undefined;
  }, [finalSchoolId]);

  // Fetch bank details when modal opens - enable query when modal is open and schoolId is available
  const { bankDetailsQuery } = useFeeStructure(schoolIdForHook, false, false);
  
  // Refetch bank details when modal opens
  useEffect(() => {
    if (open && finalSchoolId && bankDetailsQuery.refetch) {
      bankDetailsQuery.refetch();
    }
  }, [open, finalSchoolId]);

  // Local state for form fields
  const [bankName, setBankName] = useState<string>("");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [ifscCode, setIfscCode] = useState<string>("");
  const [ifscError, setIfscError] = useState<boolean>(false);

  // Initialize form when modal opens - fetch and populate bank details if they exist
  useEffect(() => {
    if (open && finalSchoolId) {
      // Reset form first
      setBankName("");
      setAccountNumber("");
      setIfscCode("");
      setIfscError(false);

      // Fetch bank details - they will be populated when query completes
      if (bankDetailsQuery.data) {
        const bankData = bankDetailsQuery.data;
        setBankName(bankData.bankName || "");
        setAccountNumber(bankData.accountNo || "");
        setIfscCode(bankData.ifscCode || "");
        // Validate IFSC if it exists
        if (bankData.ifscCode) {
          const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
          setIfscError(!ifscRegex.test(bankData.ifscCode.toUpperCase()));
        }
      }
    } else if (open && !finalSchoolId) {
      // If modal opens but no schoolId, reset form
      setBankName("");
      setAccountNumber("");
      setIfscCode("");
      setIfscError(false);
    }
  }, [open, finalSchoolId, bankDetailsQuery.data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!finalSchoolId) {
      showError(t("school_id_is_required") || "School ID is required");
      return;
    }

    if (!bankName.trim()) {
      showError(t("bank_name_is_required") || "Bank name is required");
      return;
    }

    if (!accountNumber.trim()) {
      showError(t("account_number_is_required") || "Account number is required");
      return;
    }

    if (!ifscCode.trim()) {
      showError(t("ifsc_code_is_required") || "IFSC code is required");
      return;
    }

    // Validate IFSC code format (11 characters, alphanumeric)
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    const trimmedIfsc = ifscCode.trim().toUpperCase();
    if (!ifscRegex.test(trimmedIfsc)) {
      setIfscError(true);
      showError(t("invalid_ifsc_code_format") || "IFSC code must be 11 characters (e.g., ABCD0123456)");
      return;
    }
    setIfscError(false);

    const formData: BankDetailsDto = {
      id: finalSchoolId as number,
      bankName: bankName.trim(),
      accountNo: accountNumber.trim(),
      ifscCode: ifscCode.trim().toUpperCase(),
    };

    onSubmit(formData);
  };

  // Validate IFSC format for form validation
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const isIfscValid = ifscCode.trim() === "" || ifscRegex.test(ifscCode.trim().toUpperCase());

  const isFormValid =
    finalSchoolId !== undefined &&
    bankName.trim() !== "" &&
    accountNumber.trim() !== "" &&
    ifscCode.trim() !== "" &&
    isIfscValid;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fb",
          borderBottom: "2px solid #e0e7ff",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {bankDetailsQuery.data 
            ? (t("edit_bank_details") || "Edit Bank Details")
            : (t("add_bank_details") || "Add Bank Details")}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {bankDetailsQuery.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bank_name") || "Bank Name"}
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  required
                  variant="outlined"
                />
              </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("account_number") || "Account Number"}
                value={accountNumber}
                onChange={(e) => {
                  // Only allow numbers
                  const value = e.target.value.replace(/\D/g, "");
                  setAccountNumber(value);
                }}
                required
                variant="outlined"
                inputProps={{ maxLength: 18 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("ifsc_code") || "IFSC Code"}
                value={ifscCode}
                onChange={(e) => {
                  // Convert to uppercase and allow only alphanumeric
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                  setIfscCode(value);
                  // Validate format in real-time
                  if (value.length > 0) {
                    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
                    setIfscError(!ifscRegex.test(value));
                  } else {
                    setIfscError(false);
                  }
                }}
                onBlur={() => {
                  // Validate on blur if field has value
                  if (ifscCode.trim().length > 0) {
                    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
                    setIfscError(!ifscRegex.test(ifscCode.trim().toUpperCase()));
                  }
                }}
                required
                variant="outlined"
                inputProps={{ maxLength: 11 }}
                error={ifscError}
                helperText={
                  <span style={{ color: ifscError ? "#d32f2f" : "rgba(0, 0, 0, 0.6)" }}>
                    {t("ifsc_code_format_help") || "Format: ABCD0123456 (11 characters)"}
                  </span>
                }
              />
            </Grid>
          </Grid>
        </form>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={handleClose} color="inherit">
          {t("cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid}
          sx={{
            backgroundColor: "#3092e3",
            "&:hover": { backgroundColor: "primary.main" },
          }}
        >
          {bankDetailsQuery.data 
            ? (t("update") || "Update")
            : (t("add") || "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankDetailsModal;

